/**
 * Tempo 协议处理器
 * 处理 tempo:// 协议链接的解析和跳转逻辑
 */

import { NavigateFunction } from 'react-router-dom';
import { parseTempoUrl, TempoEntityType, isTempoUrl } from '../constants/tempoProtocol';

// 全局任务编辑状态类型
export interface GlobalTaskEditState {
  taskId: string | null;
  isOpen: boolean;
  triggerSource: 'hyperlink' | 'normal';
}

// 协议处理器类
export class TempoProtocolHandler {
  private navigate: NavigateFunction;
  private setGlobalTaskEdit: (state: GlobalTaskEditState) => void;

  constructor(
    navigate: NavigateFunction,
    setGlobalTaskEdit: (state: GlobalTaskEditState) => void
  ) {
    this.navigate = navigate;
    this.setGlobalTaskEdit = setGlobalTaskEdit;
  }

  /**
   * 处理 tempo:// 协议导航
   */
  handleTempoNavigation = (url: string): boolean => {
    // 检查是否为 tempo:// 协议
    if (!isTempoUrl(url)) {
      return false;
    }

    // 检查是否在 v3 版本中
    if (!this.isV3Version()) {
      console.warn('Tempo protocol links are only supported in v3 version');
      return false;
    }

    // 解析 URL
    const parsed = parseTempoUrl(url);
    if (!parsed.isValid) {
      console.error('Invalid tempo:// URL:', url);
      this.showError('链接格式无效');
      return false;
    }

    // 根据实体类型处理跳转
    try {
      switch (parsed.entityType) {
        case TempoEntityType.PROJECT:
          this.handleProjectNavigation(parsed.entityId);
          break;
        case TempoEntityType.TASK:
          this.handleTaskNavigation(parsed.entityId);
          break;
        default:
          console.error('Unsupported entity type:', parsed.entityType);
          this.showError('不支持的链接类型');
          return false;
      }
      return true;
    } catch (error) {
      console.error('Error handling tempo navigation:', error);
      this.showError('跳转失败，请重试');
      return false;
    }
  };

  /**
   * 处理项目跳转
   */
  private handleProjectNavigation = (projectId: string): void => {
    // 导航到项目详情页
    this.navigate(`/v3/projects/${projectId}`);
  };

  /**
   * 处理任务跳转
   */
  private handleTaskNavigation = (taskId: string): void => {
    // 设置全局任务编辑状态
    this.setGlobalTaskEdit({
      taskId,
      isOpen: true,
      triggerSource: 'hyperlink'
    });
  };

  /**
   * 检查当前是否在 v3 版本
   */
  private isV3Version = (): boolean => {
    return window.location.pathname.includes('/v3/');
  };

  /**
   * 显示错误提示
   */
  private showError = (message: string): void => {
    // 这里可以集成 toast 或其他通知系统
    console.error('Tempo Protocol Error:', message);
    // TODO: 集成实际的错误提示组件
  };
}

/**
 * 创建协议处理器实例的工厂函数
 */
export const createTempoProtocolHandler = (
  navigate: NavigateFunction,
  setGlobalTaskEdit: (state: GlobalTaskEditState) => void
): TempoProtocolHandler => {
  return new TempoProtocolHandler(navigate, setGlobalTaskEdit);
};
